"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TableLayout = exports.GridLayout = exports.ListLayout = exports.Virtualizer = void 0;
var react_aria_components_1 = require("react-aria-components");
/**
 * A virtualizer component that enables efficient rendering of large collections by only rendering items that are currently visible.
 * Collection components such as ListBox, GridList, and Table can be virtualized by wrapping them in a Virtualizer.
 *
 * @see {@link https://react-spectrum.adobe.com/react-aria/Virtualizer.html}
 * @see {@link https://compass.circleci.com/?path=/story/rac-virtualizer--default}
 *
 * @example
 * ```tsx
 * <Virtualizer>
 *   <ListBox>
 *     <ListLayout rowHeight={48} gap={8}>
 *       {items.map(item => (
 *         <ListBoxItem key={item.id}>{item.name}</ListBoxItem>
 *       ))}
 *     </ListLayout>
 *   </ListBox>
 * </Virtualizer>
 * ```
 *
 * @note Virtualized components must have a defined size (explicit CSS width/height or implicit size bounded by an ancestor element).
 * Without a bounded size, all items will be rendered to the DOM, negating the performance benefits of virtualized scrolling.
 */
/**
 * A layout component for virtualized lists that provides fixed or estimated row heights.
 * Optimized for vertical scrolling with support for section headers and loaders.
 *
 * @example
 * ```tsx
 * <ListLayout
 *   rowHeight={48}
 *   estimatedRowHeight={40}
 *   headingHeight={32}
 *   gap={8}
 *   padding={16}
 * >
 *   {items.map(item => (
 *     <ListBoxItem key={item.id}>{item.name}</ListBoxItem>
 *   ))}
 * </ListLayout>
 * ```
 *
 * @param rowHeight - The fixed height of a row in px. Defaults to 48
 * @param estimatedRowHeight - The estimated height of a row, when row heights are variable
 * @param headingHeight - The fixed height of a section header in px. Defaults to 48
 * @param estimatedHeadingHeight - The estimated height of a section header, when the height is variable
 * @param loaderHeight - The fixed height of a loader element in px. Defaults to 48
 * @param dropIndicatorThickness - The thickness of the drop indicator. Defaults to 2
 * @param gap - The gap between items. Defaults to 0
 * @param padding - The padding around the list. Defaults to 0
 */
/**
 * A layout component for virtualized grids that provides flexible item sizing and column management.
 * Optimized for both horizontal and vertical scrolling with support for aspect ratio preservation.
 *
 * @example
 * ```tsx
 * <GridLayout
 *   minItemSize={{ width: 200, height: 200 }}
 *   maxItemSize={{ width: 400, height: 400 }}
 *   preserveAspectRatio={true}
 *   minSpace={{ width: 18, height: 18 }}
 *   maxColumns={4}
 * >
 *   {items.map(item => (
 *     <GridItem key={item.id}>{item.name}</GridItem>
 *   ))}
 * </GridLayout>
 * ```
 *
 * @param minItemSize - The minimum item size. Defaults to 200x200
 * @param maxItemSize - The maximum item size. Defaults to Infinity
 * @param preserveAspectRatio - Whether to preserve the aspect ratio of the minItemSize. Defaults to false
 * @param minSpace - The minimum space required between items. Defaults to 18x18
 * @param maxColumns - The maximum number of columns. Defaults to Infinity
 * @param dropIndicatorThickness - The thickness of the drop indicator. Defaults to 2
 */
/**
 * A layout component for virtualized tables that provides fixed or estimated row heights.
 * Optimized for vertical scrolling with support for section headers and loaders.
 *
 * @example
 * ```tsx
 * <TableLayout
 *   rowHeight={48}
 *   estimatedRowHeight={40}
 *   headingHeight={32}
 *   gap={8}
 *   padding={16}
 * >
 *   <Table>
 *     {items.map(item => (
 *       <TableRow key={item.id}>
 *         <TableCell>{item.name}</TableCell>
 *         <TableCell>{item.value}</TableCell>
 *       </TableRow>
 *     ))}
 *   </Table>
 * </TableLayout>
 * ```
 *
 * @param rowHeight - The fixed height of a row in px. Defaults to 48
 * @param estimatedRowHeight - The estimated height of a row, when row heights are variable
 * @param headingHeight - The fixed height of a section header in px. Defaults to 48
 * @param estimatedHeadingHeight - The estimated height of a section header, when the height is variable
 * @param loaderHeight - The fixed height of a loader element in px. Defaults to 48
 * @param dropIndicatorThickness - The thickness of the drop indicator. Defaults to 2
 * @param gap - The gap between items. Defaults to 0
 * @param padding - The padding around the list. Defaults to 0
 */
exports.Virtualizer = react_aria_components_1.Virtualizer;
exports.ListLayout = react_aria_components_1.ListLayout;
exports.GridLayout = react_aria_components_1.GridLayout;
exports.TableLayout = react_aria_components_1.TableLayout;
//# sourceMappingURL=Virtualizer.js.map